import {
  ERROR_MSG_MAX_CHARS,
  ERROR_STACK_MAX_CHARS,
  ERRORS_MAX,
  LIVE_ERRORS_MAX,
  LIVE_EVENTS_MAX,
  SESSION_GAP_MS,
} from "./config";
import { DayStore, type DayRecord } from "./buckets";
import { MetricSamples } from "./metrics";
import type { CapturedError } from "./snapshot";

// 記録の中枢。日次集計 (DayStore) とライブバッファ (パネル表示専用) を持つ。
// すべてのメソッドは now を注入可能 (テスト用)。永続化は呼び出し側 (index.ts) が
// onDirty コールバック経由でスケジュールする。

export interface LiveEvent {
  t: string; // HH:MM:SS
  type: string;
  target?: string;
}

export interface MetricSummary {
  last: number;
  avg: number;
  p95: number;
}

const formatTime = (ms: number): string => {
  const d = new Date(ms);
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const pushBounded = <T>(buffer: T[], item: T, max: number): void => {
  buffer.push(item);
  if (buffer.length > max) {
    buffer.splice(0, buffer.length - max);
  }
};

export class Collector {
  readonly store = new DayStore();
  lastInteractionAt: number | null = null;
  sessionStartAt: number | null = null;

  private readonly liveEvents: LiveEvent[] = [];
  private readonly liveErrors: LiveEvent[] = [];
  private readonly errors: CapturedError[] = [];
  private readonly metrics: Record<string, MetricSamples> = {
    routeMs: new MetricSamples(),
    renderMs: new MetricSamples(),
  };

  constructor(private readonly onDirty: () => void = () => {}) {}

  /**
   * 実ユーザー入力 (pointer/keyboard)。セッション区切りと途絶判定の基準時刻を更新する。
   * SESSION_GAP_MS を超えたギャップで前セッションを確定し長さヒストグラムへ積む。
   */
  interaction(now: number = Date.now()): void {
    if (
      this.lastInteractionAt === null ||
      this.sessionStartAt === null ||
      now - this.lastInteractionAt > SESSION_GAP_MS
    ) {
      this.finalizeSession();
      this.store.session(now);
      this.sessionStartAt = now;
    }
    this.lastInteractionAt = now;
    this.onDirty();
  }

  /** pointerdown のみ。interaction に加えてタップ系カウンタ (taps/hourly/byScreen) を更新 */
  tap(screen: string, now: number = Date.now()): void {
    this.interaction(now);
    this.store.tap(screen, now);
  }

  /** 進行中セッションを確定する (アプリ終了前 or 次セッション開始時) */
  finalizeSession(): void {
    if (this.sessionStartAt !== null && this.lastInteractionAt !== null) {
      this.store.sessionLength(this.lastInteractionAt - this.sessionStartAt, this.sessionStartAt);
    }
    this.sessionStartAt = null;
  }

  recordSearch(facilityId: string | null | undefined, now: number = Date.now()): void {
    this.store.search(facilityId, now);
    this.onDirty();
  }

  recordError(err: unknown, context?: string, now: number = Date.now()): void {
    const e = err instanceof Error ? err : new Error(String(err));
    const message = [context, e.message].filter(Boolean).join(": ").slice(0, ERROR_MSG_MAX_CHARS);
    const stackHead = e.stack
      ? e.stack
          .split("\n")
          .slice(1, 3) // 1行目は "Name: message" の重複なので除く
          .map((line) => line.trim())
          .join(" | ")
          .slice(0, ERROR_STACK_MAX_CHARS)
      : undefined;
    const captured: CapturedError = {
      t: formatTime(now),
      name: e.name || "Error",
      message,
      ...(stackHead ? { stackHead } : {}),
    };
    pushBounded(this.errors, captured, ERRORS_MAX);
    pushBounded(
      this.liveErrors,
      { t: captured.t, type: captured.name, target: message },
      LIVE_ERRORS_MAX,
    );
    this.store.error(now);
    this.onDirty();
  }

  /** ライブイベント (パネル表示専用。QRペイロード・日次集計には入らない) */
  recordEvent(type: string, target?: string, now: number = Date.now()): void {
    pushBounded(
      this.liveEvents,
      { t: formatTime(now), type, target: target?.slice(0, 48) },
      LIVE_EVENTS_MAX,
    );
  }

  recordMetric(name: string, valueMs: number, now: number = Date.now()): void {
    let samples = this.metrics[name];
    if (!samples) {
      samples = new MetricSamples();
      this.metrics[name] = samples;
    }
    samples.push(valueMs);
    if (name === "routeMs") this.store.metric("route", valueMs, now);
    if (name === "renderMs") this.store.metric("render", valueMs, now);
    this.onDirty();
  }

  // --- 読み出し ---

  days(): DayRecord[] {
    return this.store.list();
  }

  capturedErrors(): CapturedError[] {
    return [...this.errors];
  }

  getLiveEvents(): LiveEvent[] {
    return [...this.liveEvents];
  }

  getLiveErrors(): LiveEvent[] {
    return [...this.liveErrors];
  }

  metricSummary(name: string): MetricSummary | null {
    return this.metrics[name]?.summary() ?? null;
  }

  /**
   * 収集済み統計 (日次バケット・エラー・ライブバッファ) のみ破棄する。
   * lastInteractionAt はタッチ途絶判定用に維持する。進行中セッションは破棄し、
   * reset 後の次の操作から新しいセッションとして集計する。
   */
  reset(): void {
    this.store.reset();
    this.errors.length = 0;
    this.liveEvents.length = 0;
    this.liveErrors.length = 0;
    for (const s of Object.values(this.metrics)) s.clear();
    this.sessionStartAt = null;
    this.onDirty();
  }

  /** 永続化された状態を復元。前回起動時の未確定セッションはここで確定する */
  restore(li: number | null, ss: number | null, daysRaw: unknown): void {
    this.store.load(daysRaw);
    this.lastInteractionAt = li;
    this.sessionStartAt = ss;
    this.finalizeSession();
  }
}
